import { CharacterSchema, type Character, CHARACTER_SCHEMA_VERSION } from '../schemas/character';

/**
 * Migration di un Character salvato verso la versione corrente dello schema.
 *
 * Questo è il **single entry point** per leggere Character salvati su disco /
 * localStorage / file di import: gestisce ogni `schemaVersion` precedente e
 * la rimaneggia per produrre un Character valido contro `CharacterSchema`.
 *
 * **Pattern**: ogni volta che bumpiamo `CHARACTER_SCHEMA_VERSION`,
 * aggiungiamo qui una funzione `migrateVNtoVM(raw): unknown` (chained) e
 * aggiorniamo `migrateCharacter` per concatenarla.
 *
 * **Stato attuale**: la versione corrente è la 2. Non ci sono ancora PG
 * salvati con `schemaVersion: 1` perché il progetto è in fase di sviluppo
 * pre-persistenza utenti. La funzione `migrateV1toV2` esiste come segnaposto
 * del pattern: se mai dovesse essere chiamata in futuro, fallisce in modo
 * esplicito invece di propagare silenziosamente dati incompleti.
 */
export function migrateCharacter(raw: unknown): Character {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('migrateCharacter: input non è un oggetto');
  }
  const obj = raw as { schemaVersion?: unknown };
  const version = obj.schemaVersion;

  if (version === CHARACTER_SCHEMA_VERSION) {
    return CharacterSchema.parse(raw);
  }

  if (version === 1) {
    return CharacterSchema.parse(migrateV1toV2(raw));
  }

  throw new Error(
    `migrateCharacter: schemaVersion sconosciuta (${String(version)}); attesa <= ${CHARACTER_SCHEMA_VERSION}`,
  );
}

/**
 * Migration v1 → v2 (PR 4).
 *
 * **NON ANCORA IMPLEMENTATA** intenzionalmente: il bump v1→v2 introduce
 * `archetypeId` (obbligatorio) e `chosenAspetti`, e cambia il tipo di
 * `damage` da stringa libera a `DamageDieSchema`. Non c'è modo di derivare
 * `archetypeId` da un Character v1 senza intervento umano.
 *
 * Quando avremo un caso reale (PG v1 da migrare), questa funzione dovrà:
 *  1. estrarre `classId` e leggere `ClassDefinition.archetypes[0].id` come
 *     fallback (oppure prompt UI per la scelta)
 *  2. parsare la stringa `damage` in `DamageDieSchema` (validare contro l'enum)
 *  3. inizializzare `chosenAspetti: []` (lista vuota, il giocatore le
 *     ricompila manualmente)
 *  4. bumpare `schemaVersion` a 2
 *
 * Per ora lancia: non vogliamo accettare silenziosamente dati v1 incompleti.
 */
function migrateV1toV2(_raw: unknown): unknown {
  throw new Error(
    'migrateV1toV2: migration v1→v2 non ancora implementata. ' +
      'Nessun PG v1 salvato esiste oggi. ' +
      'Implementare quando arriverà un caso reale (vedi commenti nel sorgente).',
  );
}
